/**
 * FLY SINGLE MISSION — the original's two-column button grid, verbatim.
 *
 * `docs/game/ui-reference/fly-single-mission.png` shows thirteen labels in
 * seven rows plus `RETURN TO MAIN`, and the correction filed in
 * `ui-menus.md` is that this list is a *submenu*, not the top-level menu it
 * had been documented as. All fourteen slots are here in the capture's own
 * order, because the point of AD-009 is that the 320×200 frame the player
 * sees is the 1990 one.
 *
 * Ten of them fly — eight straight to a briefing, `FLY A HISTORIC MISSION`
 * by way of its own `SELECT HISTORIC MISSION:` popup (`historic-picker.ts`),
 * and `BEST MISSIONS` opens the score table (`best-missions.ts`). The other
 * two custom-mission slots are drawn in the same grid, in the same type, and
 * refuse with the reason — one dialog each, in the original's own popup
 * style. That is the same discipline `MISSION_SUPPORT` already applies to the
 * mission types: a menu that silently omits half the game is worse than one
 * that says why, and the reasons for any deferred *type* come straight out of
 * the engine's own
 * register rather than being restated here.
 *
 * **Two catalog entries have no slot, and both are the capture's doing.**
 * SCRAMBLE: `PS.EXE` carries it as the tenth entry in its *mission-type* list,
 * and the port generates it — but the capture this grid reproduces has no
 * SCRAMBLE button, so adding one would be inventing a slot the original never
 * drew. It is reachable from a career assignment and from `/fly?m=scramble`.
 * And `intercept-zeppelin`: `PS.EXE` lists HUNT A ZEPPELIN *twice*
 * (docs/game/missions.md), which is what resolves the base game's two Zeppelin
 * types; this screen's capture shows the label once, so the grid's Zeppelin
 * button flies *Destroy a Zeppelin* and the intercept is reachable at
 * `/fly?m=intercept-zeppelin`. `MISSION_CATALOG_IDS` is the honest list of what
 * this port can fly; this grid is the honest picture of what the original drew.
 *
 * **DOGFIGHT A SQUADRON went live** once `ELITE.DAT` and `ORDINARY.DAT` were
 * decoded (`campaign/squadron-strength.ts`) and `TOTAL AIRCRAFT IN FLIGHT` and
 * `FORMATION` stopped being decorative — which is exactly the pair of things its
 * refusal named. It flies `dogfight-squadron`; the slot's popup is gone rather
 * than reworded, because the systems it was waiting on now exist.
 */

import { MISSION_BUILDER_LABELS, MISSION_SUPPORT, type MissionType } from "@red-opus/engine";
import { boardFrame, type BoardSpec } from "../render/board-chrome.ts";

/** Measured off `fly-single-mission.png`: board (14,17,291,169), two 130px columns 9px apart, the last row 25px below the sixth instead of 20. */
export const MISSION_GRID_ROWS: readonly number[] = [20, 40, 60, 80, 100, 120, 145];

const GRID_LAYOUT = { rows: MISSION_GRID_ROWS, columns: 2, columnWidth: 130, columnGap: 9 } as const;

export const MISSION_GRID_BOARD: BoardSpec = {
  ...GRID_LAYOUT,
  frame: boardFrame(14, 17, GRID_LAYOUT, 6),
  ridgeInsets: [39, 51],
  footerInsets: [54, 72],
};

export const MISSION_GRID_BACKGROUND = "screens/MAINMENU.SCR";

/**
 * What a grid slot leads to: a mission the port generates, the way back, the
 * Best Missions score table, or a dialog naming the system that is missing.
 * `deferred` slots take their prose from `MISSION_SUPPORT`; `absent` ones are
 * modes with no `MissionType` at all (the Historic Encounters, the squadron
 * dogfight, the Mission Builder's two entries) and carry their own line.
 */
export type GridTarget =
  | { readonly kind: "mission"; readonly catalogId: string }
  | { readonly kind: "return" }
  | { readonly kind: "bestMissions" }
  | { readonly kind: "deferred"; readonly type: MissionType }
  | { readonly kind: "absent"; readonly reason: readonly string[] }
  /**
   * A slot that opens a list of its own before it becomes a mission —
   * `FLY A HISTORIC MISSION` and its `SELECT HISTORIC MISSION:` popup. The
   * catalog id is not known until the player picks one of the eight, so the
   * slot names the picker instead of naming a mission.
   */
  | { readonly kind: "picker"; readonly picker: "historic" };

export interface GridSlot {
  readonly id: string;
  /** Verbatim from the capture. */
  readonly label: string;
  readonly target: GridTarget;
}

function absent(id: string, label: string, reason: readonly string[]): GridSlot {
  return { id, label, target: { kind: "absent", reason } };
}

/**
 * No slot on *this* screen is deferred any more — the raid family was the last
 * of them, and `SCRAMBLE`, the one mission type still on the register, has no
 * button in the capture. The `deferred` target kind stays because it is the
 * bridge from `MISSION_SUPPORT` to the popup and the next type to be blocked
 * will want it back; only its one-line constructor went, because a factory with
 * no caller is worse than a slot literal.
 */
function mission(id: string, label: string, catalogId: string): GridSlot {
  return { id, label, target: { kind: "mission", catalogId } };
}

/** Row-major, exactly as the capture reads: left column then right, top row down. */
export const MISSION_GRID_SLOTS: readonly GridSlot[] = [
  { id: "historic", label: "FLY A HISTORIC MISSION", target: { kind: "picker", picker: "historic" } },
  mission("stop-bombing", "STOP A BOMBING RAID", "stop-bombers"),
  mission("dogfight-ace", "DOGFIGHT A FAMOUS ACE", "dogfight-ace"),
  mission("hunt-zeppelin", "HUNT A ZEPPELIN", "hunt-zeppelin"),
  mission("dogfight-squadron", "DOGFIGHT A SQUADRON", "dogfight-squadron"),
  mission("escort-recon", "ESCORT RECONNAISSANCE", "escort-recon"),
  mission("patrol", "PATROL THE FRONT", "patrol"),
  mission("balloon-defense", "BALLOON DEFENSE", "balloon-defense"),
  mission("escort-bombing", "ESCORT A BOMBING RAID", "escort-bombers"),
  mission("balloon-busting", "BALLOON BUSTING!", "balloon-busting"),
  absent("build-custom", "BUILD A CUSTOM MISSION", [
    "THE MISSION BUILDER IS PART 2.",
    "PART 1 IS THE BASE GAME.",
  ]),
  absent("fly-custom", "FLY A CUSTOM MISSION", [
    "READING A .CUS FILE IS PART 2,",
    "WITH THE MISSION BUILDER.",
  ]),
  { id: "best-missions", label: "BEST MISSIONS", target: { kind: "bestMissions" } },
  { id: "return", label: "RETURN TO MAIN", target: { kind: "return" } },
];

/** The slot's dialog prose, for the eight that refuse. */
export function gridSlotReason(slot: GridSlot): readonly string[] {
  if (slot.target.kind === "absent") return slot.target.reason;
  if (slot.target.kind !== "deferred") return [];
  const support = MISSION_SUPPORT[slot.target.type];
  return support.state === "deferred" ? wrapReason(support.missing) : [];
}

/** The Mission Builder's unambiguous name for a deferred type, for the dialog's own header. */
export function gridSlotTitle(slot: GridSlot): string {
  return slot.target.kind === "deferred" ? MISSION_BUILDER_LABELS[slot.target.type].toUpperCase() : slot.label;
}

export function gridSlotById(id: string): GridSlot | undefined {
  return MISSION_GRID_SLOTS.find((slot) => slot.id === id);
}

export function isGridSlotFlyable(slot: GridSlot): boolean {
  return (
    slot.target.kind === "mission" ||
    slot.target.kind === "return" ||
    slot.target.kind === "bestMissions" ||
    slot.target.kind === "picker"
  );
}

/** `MISSION_SUPPORT`'s prose is a paragraph; a popup row is about 34 characters of FONT3. */
const REASON_COLUMNS = 34;

export function wrapReason(text: string): readonly string[] {
  const lines: string[] = [];
  let line = "";
  for (const word of text.toUpperCase().split(/\s+/)) {
    if (line === "") line = word;
    else if (line.length + 1 + word.length <= REASON_COLUMNS) line = `${line} ${word}`;
    else {
      lines.push(line);
      line = word;
    }
  }
  if (line !== "") lines.push(line);
  return lines.slice(0, 6);
}
